/**
 * Enh 5 - Set Password Page.
 * Landed on by magic-link users whose has_password === false.
 * On submit, POST /api/auth/set-password with just new_password (+ optional display name).
 * On success, route by role.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Lock, User as UserIcon } from 'lucide-react';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

const SetPasswordPage = () => {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (pw1.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (pw1 !== pw2)     { setErr('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await api.post('/auth/set-password', {
        new_password: pw1,
        display_name: displayName.trim() || null,
      });
      if (refresh) await refresh();
      const role = user && user.role;
      navigate((role === 'admin' || role === 'super_admin') ? '/admin' : '/my-events');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not set password.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen dost-signature-bg-full flex items-center justify-center p-4">
      <Card className="w-full max-w-md border shadow-soft" data-testid="set-password-card">
        <CardContent className="p-8 space-y-5">
          <div className="text-center">
            <div className="w-14 h-14 mx-auto bg-primary-900 text-white rounded-2xl flex items-center justify-center mb-3">
              <Lock className="h-6 w-6" />
            </div>
            <h1 className="font-display text-2xl font-bold text-primary-900">Set your password</h1>
            <p className="text-sm text-neutral-600 mt-1">
              Signed in as <span className="font-semibold">{user && user.email}</span>. Pick a password so you can sign in without a magic link next time.
            </p>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-secondary-700 flex items-center gap-1.5">
                <UserIcon className="h-3 w-3" /> Display name (optional)
              </Label>
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                     placeholder="How should we address you?"
                     data-testid="set-pw-display-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-secondary-700">New password</Label>
              <Input type="password" value={pw1} onChange={(e) => setPw1(e.target.value)}
                     placeholder="At least 8 characters" required
                     data-testid="set-pw-new" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-secondary-700">Confirm password</Label>
              <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                     placeholder="Type it again" required
                     data-testid="set-pw-confirm" />
            </div>
            {err && <div className="text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded px-3 py-2" data-testid="set-pw-error">{err}</div>}
            <Button type="submit" disabled={busy} className="w-full bg-primary-800 hover:bg-primary-900 text-white" data-testid="set-pw-submit">
              {busy ? 'Setting...' : 'Set password and continue'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetPasswordPage;
